import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button } from 'reactstrap';
import ClipLoader from 'react-spinners/ClipLoader';
import api from '../../../../Api.js';
import { connect } from 'react-redux';
import './UnitsModal.css';

const BRAND_COLORS = {
  primary: '#0e1d3f'
};

export function UnitsModal({ isOpen, toggle, course, units = [], onOpenCourseForUnit, onBack, translate = {} }){
  // Contadores por unidade (alunos únicos no curso dentro da unidade)
  const [counts, setCounts] = useState({}); // { [unitId]: number }
  const [loadingIds, setLoadingIds] = useState({}); // { [unitId]: true }
  const [unitsList, setUnitsList] = useState(Array.isArray(units) ? units : []);
  const [loadingUnits, setLoadingUnits] = useState(false);

  const unitsListKey = useMemo(() => (unitsList && unitsList.length ? unitsList.map(u => String(u.id)).join(',') : ''), [unitsList]);

  useEffect(() => {
    if (Array.isArray(units) && units.length > 0) setUnitsList(units);
  }, [units]);

  useEffect(() => {
    let mounted = true;
    const fetchUnitsForCourse = async () => {
      if (!isOpen || !course || !course.id) return;
      if (Array.isArray(units) && units.length > 0) return; 
      if (course.companies && Array.isArray(course.companies) && course.companies.length > 0) {
        if (mounted) setUnitsList(course.companies.map(u => ({ id: Number(u.id), name: u.name || `Unidade #${u.id}` })));
        return;
      }

      setLoadingUnits(true);
      try {
        // Tenta rota agregada (mais eficiente)
        const rpt = await api.getCoursesUnitsReport({ course: course.id }).catch(() => null);
        const rows = (rpt && Array.isArray(rpt.data)) ? rpt.data : [];

        if (rows.length > 0) {
          if (rows[0].companiesList && typeof rows[0].companiesList === 'string') {
            const companies = rows[0].companiesList.split(',').map(pair => {
              const [idRaw, ...nameParts] = pair.split(':');
              const id = idRaw ? idRaw.trim() : idRaw;
              const nameRaw = nameParts.join(':').trim();
              return { id: Number(id), name: nameRaw || `Unidade #${id}` };
            });
            if (mounted) setUnitsList(companies);
            setLoadingUnits(false);
            return;
          }

          if (rows[0].companies && Array.isArray(rows[0].companies) && rows[0].companies.length > 0) {
            const companies = rows[0].companies.map(c => ({ id: Number(c.id), name: c.name || `Unidade #${c.id}` }));
            if (mounted) setUnitsList(companies);
            setLoadingUnits(false);
            return;
          }
        }

        const allResp = await api.getAllClassRoom().catch(() => null);
        const classrooms = (allResp && Array.isArray(allResp.data)) ? allResp.data : [];

        const matchesCourse = (cr) => {
          return (
            String(cr.course) === String(course.id) ||
            String(cr.courseId) === String(course.id) ||
            String(cr.course_id) === String(course.id) ||
            String(cr.class_course) === String(course.id)
          );
        };

        const ids = Array.from(new Set(classrooms.filter(cr => matchesCourse(cr)).map(cr => cr.company).filter(Boolean)));
        const computed = ids.map(id => {
          const entry = classrooms.find(c => String(c.company) === String(id) && (c.companyName || c.company_name || c.companyNameFormatted));
          const nameFromClass = entry ? (entry.companyName || entry.company_name || entry.companyNameFormatted || '') : '';
          return { id, name: nameFromClass || `Unidade #${id}` };
        });

        if (mounted) setUnitsList(computed);
      } catch (err) {
        console.error('Erro ao buscar unidades para o curso no modal UnitsModal:', err);
      } finally {
        if (mounted) setLoadingUnits(false);
      }
    };
    fetchUnitsForCourse();
    return () => { mounted = false; };
  }, [isOpen, course && course.id, units]);

  useEffect(() => {
    if (!isOpen || !course || !course.id || !unitsList || unitsList.length === 0) {
      setCounts({});
      setLoadingIds({});
      return;
    }

    let cancelled = false;
    const limit = 4; // concorrência máxima de requests simultâneos
    const ids = unitsList.map(u => u.id).filter(Boolean);

    // inicializa estado de loading
    const initialLoading = {};
    ids.forEach(id => { initialLoading[id] = true; });
    setLoadingIds(initialLoading);

    const nextQueue = async () => {
      let inFlight = 0;
      let idx = 0;
      return await new Promise(resolve => {
        const tick = () => {
          if (cancelled) return resolve();
          if (idx >= ids.length && inFlight === 0) return resolve();
          while (inFlight < limit && idx < ids.length) {
            const unitId = ids[idx++];
            inFlight++;
            (async () => {
              try {
                const resp = await api.getCourseClasses(course.id, unitId);
                const data = (resp && resp.data) ? resp.data : {};
                const students = Array.isArray(data.students) ? data.students : [];
                if (!cancelled) {
                  setCounts(prev => ({ ...prev, [unitId]: students.length }));
                }
              } catch (err) {
                // silencioso; se falhar, mostramos vazio
                if (!cancelled) {
                  setCounts(prev => ({ ...prev, [unitId]: 0 }));
                }
              } finally {
                if (!cancelled) setLoadingIds(prev => ({ ...prev, [unitId]: false }));
                inFlight--;
                setTimeout(tick, 0); // cede ao event loop
              }
            })();
          }
        };
        tick();
      });
    };

    nextQueue();
    return () => { cancelled = true; };
  }, [isOpen, course && course.id, unitsListKey]);

  return (
    <Modal
      className="modal-dialog-centered"
      size="xl"
      isOpen={isOpen}
      toggle={toggle}
      contentClassName="units-modal"
      style={{ maxWidth: '90%', maxHeight: '85vh' }}
    >
      <div className="modal-header d-flex align-items-center">
        <h5 className="modal-title mb-0 text-white">{translate['unidades_usando'] || `Unidades usando: ${course?.name || course?.id}`}</h5>
        <button aria-label="Close" className="close text-white" data-dismiss="modal" type="button" onClick={toggle}><span aria-hidden={true}>×</span></button>
      </div>
      <div className="modal-body" style={{ maxHeight: 'calc(85vh - 180px)', overflowY: 'auto' }}>
        {(!unitsList || unitsList.length === 0) ? (
          <div className="text-center text-muted py-4 empty-state">
            <i className="fas fa-school text-muted mb-3" aria-hidden="true"></i>
            <div>{translate['nenhuma_unidade_encontrada'] || 'Nenhuma unidade encontrada para este curso.'}</div>
          </div>
        ) : (
          <div className="unit-list">
            {unitsList.map(u => (
              <div key={`unit-${u.id}`} className="unit-item d-flex align-items-center">
                <div className="unit-icon" aria-hidden="true">
                  <i className="fas fa-university"></i>
                </div>
                <div className="unit-info flex-fill">
                  <div className="unit-name">{u.name}</div>
                  <div className="unit-meta text-muted">ID: {u.id}</div>
                  <div className="unit-meta text-muted d-flex align-items-center">
                    <i className="fas fa-user-graduate mr-2" aria-hidden="true"></i>
                    <span className="mr-2">{translate['alunos'] || 'Alunos'}:</span>
                    {loadingIds[u.id] ? (
                      <ClipLoader size={16} color={BRAND_COLORS.primary} loading={true} />
                    ) : (
                      <span className="unit-count">{counts[u.id] ?? 0}</span>
                    )}
                  </div>
                </div>
                <div className="unit-actions">
                  <Button
                    size="sm"
                    color="primary"
                    outline
                    className="d-inline-flex align-items-center unit-action-btn"
                    onClick={() => onOpenCourseForUnit(course?.id, u.id, course?.name, u.name)}
                  >
                    <i className="fas fa-eye mr-1" aria-hidden="true"></i>
                    {translate['ver_turmas'] || 'Ver turmas'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="modal-footer d-flex justify-content-between align-items-center w-100">
        <div>
          {onBack && (
            <Button
              color="primary"
              outline
              size="sm"
              className="d-inline-flex align-items-center footer-btn"
              onClick={onBack}
            >
              <i className="fas fa-arrow-left mr-1" aria-hidden="true"></i>
              {translate['voltar'] || 'Voltar'}
            </Button>
          )}
        </div>
        <div>
          <Button
            color="primary"
            size="sm"
            className="d-inline-flex align-items-center footer-btn"
            onClick={toggle}
          >
            <i className="fas fa-times mr-1" aria-hidden="true"></i>
            {translate['fechar'] || 'Fechar'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default connect(state => ({ translate: state.translate }))(UnitsModal);
